/**
 * services/eventService.ts - Event and Promotion API Service
 */

import apiClient from './apiClient';

export type EventStatus =
  | 'draft'
  | 'scheduled'
  | 'active'
  | 'paused'
  | 'expired'
  | 'completed'
  | 'cancelled';

export const PROMOTION_STATUSES: { value: EventStatus; label: string }[] = [
  { value: 'draft', label: 'Draft' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'active', label: 'Active' },
  { value: 'paused', label: 'Paused' },
  { value: 'expired', label: 'Expired' },
];

export const EVENT_STATUSES: { value: EventStatus; label: string }[] = [
  { value: 'draft', label: 'Draft' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'active', label: 'Active' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

export interface Event {
  id?: string;
  _id?: string;
  name: string;
  type: 'event' | 'promotion';
  description?: string;
  startDate: Date;
  endDate: Date;
  status: EventStatus;
  // Promotion UI fields
  title?: string;
  image?: string;
  promotionType?: 'banner' | 'popup' | 'inline' | 'sticky';
  targetAudience?: 'all' | 'new' | 'returning' | 'vip' | 'section-specific';
  actionUrl?: string;
  actionText?: string;
  priority?: number;
  // Analytics
  analytics?: {
    impressions?: number;
    clicks?: number;
    conversions?: number;
    ctr?: number;
  };
  // Event details
  eventDetails?: {
    teams?: string[];
    venue?: string;
    capacity?: number;
    ticketsSold?: number;
  };
  // Promotion details
  promotionDetails?: {
    discountType?: 'percentage' | 'fixed' | 'bogo' | 'free-item';
    discountValue?: number;
    applicableVendors?: string[];
    minOrderAmount?: number;
    maxDiscount?: number;
    code?: string;
    usageCount?: number;
    usageLimit?: number;
  };
  createdBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

class EventService {
  private basePath = '/events';

  async getAll(params?: { type?: string; status?: string }): Promise<Event[]> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.type) queryParams.append('type', params.type);
      if (params?.status) queryParams.append('status', params.status);

      const url = queryParams.toString()
        ? `${this.basePath}?${queryParams.toString()}`
        : this.basePath;

      return await apiClient.get<Event[]>(url);
    } catch (error) {
      console.error('Error fetching events:', error);
      throw error;
    }
  }

  async getById(id: string): Promise<Event> {
    try {
      return await apiClient.get<Event>(`${this.basePath}/${id}`);
    } catch (error) {
      console.error('Error fetching event:', error);
      throw error;
    }
  }

  async create(eventData: Omit<Event, '_id'>): Promise<Event> {
    try {
      return await apiClient.post<Event>(this.basePath, eventData);
    } catch (error) {
      console.error('Error creating event:', error);
      throw error;
    }
  }

  async update(id: string, eventData: Partial<Event>): Promise<Event> {
    try {
      return await apiClient.put<Event>(`${this.basePath}/${id}`, eventData);
    } catch (error) {
      console.error('Error updating event:', error);
      throw error;
    }
  }

  async delete(id: string): Promise<void> {
    try {
      await apiClient.delete(`${this.basePath}/${id}`);
    } catch (error) {
      console.error('Error deleting event:', error);
      throw error;
    }
  }

  async getActivePromotions(): Promise<Event[]> {
    try {
      return await apiClient.get<Event[]>(`${this.basePath}/promotions/active`);
    } catch (error) {
      console.error('Error fetching active promotions:', error);
      throw error;
    }
  }

  async trackClick(id: string): Promise<{ message: string; analytics: any }> {
    try {
      return await apiClient.post(`${this.basePath}/${id}/track-click`, {});
    } catch (error) {
      console.error('Error tracking click:', error);
      throw error;
    }
  }

  async trackImpression(id: string): Promise<{ message: string; analytics: any }> {
    try {
      return await apiClient.post(`${this.basePath}/${id}/track-impression`, {});
    } catch (error) {
      console.error('Error tracking impression:', error);
      throw error;
    }
  }

  async trackConversion(id: string): Promise<{ message: string; analytics: any }> {
    try {
      return await apiClient.post(`${this.basePath}/${id}/track-conversion`, {});
    } catch (error) {
      console.error('Error tracking conversion:', error);
      throw error;
    }
  }
}

const eventService = new EventService();
export default eventService;
